import { create } from 'zustand';
import { markingApi, type BufferCode, type ScanAction } from '../api/marking';

interface ScanBufferState {
  items: BufferCode[];
  count: number;
  loading: boolean;
  selectedCodes: string[];
  drawerOpen: boolean;

  // internal
  _countPollInterval: number | null;
  _itemsPollInterval: number | null;

  refreshCount: () => Promise<void>;
  refreshItems: () => Promise<void>;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleSelected: (marking_code: string) => void;
  setSelected: (codes: string[]) => void;
  selectAll: () => void;
  clearSelected: () => void;
  deleteSelected: () => Promise<{ deleted: number; errors: { marking_code: string; detail: string }[] }>;
  resolveSelected: (action: ScanAction) => Promise<{ resolved: number; already: string[]; errors: { marking_code: string; detail: string }[] }>;

  startPolling: () => void;
  stopPolling: () => void;
}

const useScanBufferStore = create<ScanBufferState>((set, get) => ({
  items: [],
  count: 0,
  loading: false,
  selectedCodes: [],
  drawerOpen: false,
  _countPollInterval: null,
  _itemsPollInterval: null,

  async refreshCount() {
    try {
      const r = await markingApi.getBufferCount();
      set({ count: r.data.count });
    } catch {
      // not authenticated / no permission — тихо
    }
  },

  async refreshItems() {
    set({ loading: true });
    try {
      const r = await markingApi.getBuffer();
      set({ items: r.data, count: r.data.length });
    } finally {
      set({ loading: false });
    }
  },

  openDrawer() {
    set({ drawerOpen: true });
    get().refreshItems();
    if (get()._itemsPollInterval === null) {
      const h = window.setInterval(async () => {
        // тихое обновление без spinner — сравниваем данные и обновляем только если изменились
        try {
          const r = await markingApi.getBuffer();
          const newItems = r.data;
          const cur = get().items;
          const changed =
            newItems.length !== cur.length ||
            newItems.some((item, i) => item.marking_code !== cur[i]?.marking_code);
          if (changed) set({ items: newItems, count: newItems.length });
        } catch {}
      }, 5000);
      set({ _itemsPollInterval: h });
    }
  },

  closeDrawer() {
    set({ drawerOpen: false, selectedCodes: [] });
    const h = get()._itemsPollInterval;
    if (h !== null) {
      clearInterval(h);
      set({ _itemsPollInterval: null });
    }
  },

  toggleSelected(marking_code) {
    const cur = get().selectedCodes;
    set({
      selectedCodes: cur.includes(marking_code)
        ? cur.filter(x => x !== marking_code)
        : [...cur, marking_code],
    });
  },

  setSelected(codes) {
    set({ selectedCodes: codes });
  },

  selectAll() {
    set({ selectedCodes: get().items.map(x => x.marking_code) });
  },

  clearSelected() {
    set({ selectedCodes: [] });
  },

  async deleteSelected() {
    const codes = get().selectedCodes;
    if (codes.length === 0) return { deleted: 0, errors: [] };
    const deletedCodes: string[] = [];
    const errors: { marking_code: string; detail: string }[] = [];
    for (const code of codes) {
      try {
        await markingApi.removeFromBuffer(code);
        deletedCodes.push(code);
      } catch (e: any) {
        errors.push({
          marking_code: code,
          detail: e?.response?.data?.detail || 'Не удалось убрать из буфера',
        });
      }
    }
    set({
      items: get().items.filter(s => !deletedCodes.includes(s.marking_code)),
      selectedCodes: get().selectedCodes.filter(code => !deletedCodes.includes(code)),
      count: Math.max(0, get().count - deletedCodes.length),
    });
    return { deleted: deletedCodes.length, errors };
  },

  async resolveSelected(action) {
    const codes = get().selectedCodes;
    if (codes.length === 0) return { resolved: 0, already: [], errors: [] };
    const r = await markingApi.resolveCodesBulk(codes, action);
    const failed = new Set((r.data.errors || []).map(e => e.marking_code));
    const okCodes = codes.filter(c => !failed.has(c));
    // оптимистично удалить из списка только успешно разрешённые
    set({
      items: get().items.filter(s => !okCodes.includes(s.marking_code)),
      selectedCodes: [],
      count: Math.max(0, get().count - r.data.resolved),
    });
    return { resolved: r.data.resolved, already: r.data.already || [], errors: r.data.errors || [] };
  },

  startPolling() {
    if (get()._countPollInterval !== null) return;
    get().refreshCount();
    get().refreshItems();
    const h = window.setInterval(() => {
      if (!get().drawerOpen) {
        get().refreshCount();
        get().refreshItems();
      }
    }, 5000);
    set({ _countPollInterval: h });
  },

  stopPolling() {
    const h = get()._countPollInterval;
    if (h !== null) {
      clearInterval(h);
      set({ _countPollInterval: null });
    }
    get().closeDrawer();
  },
}));

export default useScanBufferStore;
